import { useState } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  formListValueFormatter,
  FormSearchResultAdvanced,
  objectToCSVByKey,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  PageSection,
  getActiveInactiveStatus,
  getDefaultValueByControl,
} from '@product-framework/index';
import { useNavigate } from 'react-router-dom';
import { ServiceProviderLocationNumberSearchDisplay } from '@product-framework/components/FormFields';
import {
  locationName,
  locationNumber,
  isDefaultLocation,
  status,
} from './accessorFunctions';
import listSchema from './list.json';
import searchSchema from './search.json';
import validationSchema from './validations';
import { withServiceProviderLocation } from '../../../../../providers/ServiceProviderLocationProvider';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';

function LocationList() {
  const { formatMessage } = useLocalization();
  const store = useStore();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [resultsFound, setResultsFound] = useState(0);
  const [showSearch, setShowSearch] = useState(false);

  const { serviceProviderId, profileWarnings } = useServiceProvider();
  const { hasInvalidLocationAddress = [] } = profileWarnings;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/locations`;

  const stackablePageButtons = [
    ...(Array.isArray(hasInvalidLocationAddress?.length) &&
    hasInvalidLocationAddress?.length > 0
      ? [
          {
            label: formatMessage(
              {
                id: 'COUNT_ISSUEPLURAL',
                defaultMessage: '{count} issue{plural}',
              },
              {
                count: hasInvalidLocationAddress?.length,
                plural: hasInvalidLocationAddress?.length > 1 ? 's' : '',
              },
            ),
            Icon: FmdBadIcon,
            color: 'error',
            variant: 'contained',
            permissionName: 'CAN_SEARCH_SP_LOCATIONS',
            onClick: () => {
              const locationNumberString = objectToCSVByKey(
                hasInvalidLocationAddress,
                'locationNumber',
              );
              const filters = [
                {
                  field: 'locationNumber',
                  operator: 'in',
                  value: locationNumberString,
                },
              ];
              // ServiceProviderLocationList is store name from locations list page
              store.domain.list.set('ServiceProviderLocationList', { filters });
              navigate(`/serviceproviders/edit/${serviceProviderId}/locations`);
            },
          },
        ]
      : []),
    {
      label: formatMessage({
        id: 'CREATE_SERVICE_PROVIDER_LOCATION',
        defaultMessage: 'Create Service Provider Location',
      }),
      permissionName: 'CAN_CREATE_SP_LOCATIONS',
      Icon: IconLibrary.ServiceProviderLocation,
      variant: 'outlined',
      onClick: () => {
        navigate(
          `/serviceproviders/edit/${serviceProviderId}/locations/create`,
        );
      },
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SEARCH',
            defaultMessage: 'Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      permissionName: 'CAN_SEARCH_SP_LOCATIONS',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, locationNumber: locationNumberArg, ...other } = data;

    const formattedLocationNumber = Array.isArray(locationNumberArg)
      ? objectToCSVByKey(locationNumberArg, 'value')
      : null;

    return Object.fromEntries(
      Object.entries({
        ...(formattedLocationNumber && {
          locationNumber: formattedLocationNumber,
        }),
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderLocationList', searchState);
  };

  const preProcessSearchValues = async searchedParams => {
    const formatterMap = {
      isDefaultLocation: value => (value === 'true' ? 'Yes' : 'No'),
      status: getActiveInactiveStatus,
      locationNumber: async value =>
        ServiceProviderLocationNumberSearchDisplay(
          value,
          serviceProviderId,
          dataProvider,
          snackbar,
        ),
    };

    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const navigateLocationUrl = recordId => {
    return `/serviceproviders/edit/${serviceProviderId}/locations/${recordId}`;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderLocationList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      locationName,
      locationNumber,
      isDefaultLocation,
      status,
    },
    accessorFunctionsAdditional: {
      navigate: recordId => navigateLocationUrl(recordId),
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'ASC',
    sortBy: 'locationName',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    storeName: listProps.storeName,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    initialValues: {
      serviceProviderId,
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    storeName: listProps.storeName,
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
  };

  return (
    <ServiceProviderLayout
      selectedStepperId="LOCATIONS"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        HeaderContent={
          <StackablePageButtons
            backButton={false}
            buttons={stackablePageButtons}
          />
        }
        label={formatMessage({
          id: 'SERVICE_PROVIDER_LOCATIONS',
          defaultMessage: 'Service Provider Locations',
        })}>
        <Grid2 container direction="column" spacing={2}>
          <Grid2 xs={12}>
            <FormSearchResultAdvanced {...searchParamsProps} />
          </Grid2>

          {showSearch && (
            <PageSection
              subSection
              containerProps={{ sx: { mb: 3 } }}
              label={formatMessage({
                id: 'SEARCH_LOCATIONS',
                defaultMessage: 'Search Locations',
              })}>
              <Box>
                <CRUDComponent
                  type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                  searchProps={searchProps}
                />
              </Box>
            </PageSection>
          )}

          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Grid2>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const LocationListObservable = observer(LocationList);
export default withServiceProvider(
  withServiceProviderLocation(LocationListObservable),
);
